import React, { useState } from 'react';
import { Player } from '../../types';
import { useResponsive } from '../../hooks/useResponsive';

interface EditPlayerModalProps {
  isOpen: boolean;
  player: Player | null;
  onClose: () => void;
  onSave: (playerId: string, position: string, rating: number, name?: string, updateProfile?: boolean) => void;
  isLoading?: boolean;
}

export const EditPlayerModal: React.FC<EditPlayerModalProps> = ({
  isOpen,
  player,
  onClose,
  onSave,
  isLoading = false
}) => {
  const { isMobile } = useResponsive();
  const [position, setPosition] = useState(player?.position || 'Mediocampista');
  const [rating, setRating] = useState(player?.rating || 3);
  const [name, setName] = useState(player?.name || '');
  const [updateProfile, setUpdateProfile] = useState(false);

  // Actualizar estado cuando cambie el jugador
  React.useEffect(() => {
    if (player) {
      setPosition(player.position);
      setRating(player.rating);
      setName(player.name);
      setUpdateProfile(false);
    }
  }, [player]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (player) {
      onSave(player.id, position, rating, name, updateProfile);
    }
  };

  if (!isOpen || !player) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '400px',
        width: '100%',
        maxHeight: isMobile ? 'calc(100vh - 20px)' : '80vh',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <h2 style={{
          margin: '0 0 20px 0',
          color: '#FFFFFF',
          fontSize: isMobile ? '18px' : '20px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Editar Jugador
        </h2>

        <div style={{
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: '#2C2F33',
          borderRadius: '8px',
          border: '1px solid #40444B'
        }}>
          <div style={{ color: '#FFFFFF', fontWeight: 'bold', marginBottom: '4px' }}>
            {player.name}
          </div>
          <div style={{ color: '#B0BEC5', fontSize: '14px' }}>
            {player.position} • {'★'.repeat(player.rating)}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="edit-player-name" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Nombre en el partido:
            </label>
            <input
              id="edit-player-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: isMobile ? '14px' : '16px'
              }}
              disabled={isLoading}
              placeholder="Nombre del jugador"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={updateProfile}
                onChange={(e) => setUpdateProfile(e.target.checked)}
                disabled={isLoading}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              También cambiar mi nombre en mi perfil
            </label>
            <div style={{
              color: '#B0BEC5',
              fontSize: isMobile ? '12px' : '14px',
              marginTop: '4px',
              marginLeft: '24px'
            }}>
              Si no marcas esta opción, el cambio solo se aplicará a este partido
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="edit-player-position" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px'
            }}>
              Posición:
            </label>
            <select
              id="edit-player-position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#2C2F33',
                border: '1px solid #40444B',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: isMobile ? '14px' : '16px'
              }}
              disabled={isLoading}
            >
              <option value="Arquero">🥅 Arquero</option>
              <option value="Defensor">🛡️ Defensor</option>
              <option value="Mediocampista">⚽ Mediocampista</option>
              <option value="Delantero">🚀 Delantero</option>
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <p id="edit-player-rating-label" style={{
              display: 'block',
              marginBottom: '8px',
              color: '#FFFFFF',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px',
              margin: '0 0 8px 0'
            }}>
              Valoración:
            </p>
            <div role="group" aria-labelledby="edit-player-rating-label" style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              marginBottom: '8px'
            }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? '20px' : '24px',
                    cursor: 'pointer',
                    color: rating >= star ? '#FFD600' : '#40444B',
                    transition: 'color 0.2s ease',
                    padding: '4px'
                  }}
                  disabled={isLoading}
                >
                  ★
                </button>
              ))}
            </div>
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
              color: '#B0BEC5',
              fontSize: isMobile ? '12px' : '14px'
            }}>
              {rating} estrella{rating !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Spacer para empujar los botones hacia abajo */}
          <div style={{ flex: 1 }} />

          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: 'auto'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#40444B',
                border: 'none',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                minHeight: isMobile ? '44px' : '48px'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#4F545C';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#40444B';
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: isMobile ? '10px' : '12px',
                backgroundColor: '#00E676',
                border: 'none',
                borderRadius: '8px',
                color: '#181A1B',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                opacity: isLoading ? 0.6 : 1,
                minHeight: isMobile ? '44px' : '48px'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#00C853';
              }}
              onMouseLeave={(e) => {
                if (!isLoading) e.currentTarget.style.backgroundColor = '#00E676';
              }}
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 